import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewChild,
} from "@angular/core"
import { Helpers } from "../../../modules/helpers.module"
import { BehaviorSubject } from "rxjs"
import {
    Folder,
    Track,
    TracksItem,
    GetContentResponse,
} from "@common-lib/interfaces/api/content.api"
import { TracksService } from "@services/tracks.service"
import { PlayerService } from "@services/audio/player.service"
import {
    CurrentSong,
    SongQueue,
    toSongQueueEntry,
} from "@common-lib/classes/songQueue"
import { ParentClass } from "../../../parent"
import { MenuOption, MenuOptions } from "../menu-options/menu-options.component"
import { ContextMenuItem } from "../context-menus/local-context-menu/local-context-menu.component"
import { CdkVirtualScrollViewport } from "@angular/cdk/scrolling"
import { Misc } from "@common-lib/modules/misc"
import {
    CdkDragEnd,
    CdkDragMove,
    CdkDragStart,
    DragStartDelay,
} from "@angular/cdk/drag-drop"

export type SizeType = "s" | "xs" | "m" | "l" | "xl"

export type TableColumn = {
    index: number
    name: string
    size: SizeType
}

export type TableColumns = TableColumn[]

export type ContextMenuTypes = "folder" | "track" | "generic"

export type TrackDragState = {
    isDragging: boolean
    targetFolderID: string | undefined
}

@Component({
    selector: "app-table",
    templateUrl: "./table.component.html",
    styleUrls: ["./table.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableComponent extends ParentClass {
    public contextMenu: MenuOptions<any> | undefined
    public renamingItem: Folder | Track | undefined

    @ViewChild("renameInput") renameInput: ElementRef
    @ViewChild(CdkVirtualScrollViewport, { static: false })
    cdkVirtualScrollViewport: CdkVirtualScrollViewport

    @Input()
    public columnsConfig: TableColumns

    // Take the tableData directly from the tracks.service.ts
    // implement a loading screen for this component
    public set tableData(data: GetContentResponse) {
        this.folders = data?.folders
        this.tracks = data?.tracks
    }

    public folders: Folder[]
    public tracks: Track[]

    public hoveredItem: Folder | Track | undefined

    public get unionedData() {
        if (this.folders) {
            return this.folders.concat(this.tracks)
        }
        return this.tracks
    }

    public shouldShowEmptyState(): boolean {
        return (
            !this.isInFolder() &&
            this.folders?.length === 0 &&
            this.tracks?.length === 0
        )
    }

    public isFolder(item): boolean {
        const isFolder = item["style"] === undefined
        return isFolder
    }

    public isTrack(item): boolean {
        const isTrack = item["style"] !== undefined
        return isTrack
    }

    public isTrackTitlePending(item: Folder | Track): boolean {
        return this.isTrack(item) && (item as Track).titlePending === true
    }

    public isTrackPlaying(item: Track): boolean {
        return (
            this.player.query.currentSong?.song.id === item._id &&
            this.player.query.isPlaying
        )
    }

    public isTrackPlaybackLoading(item: Track): boolean {
        return this.playbackLoadingTrackID === item._id
    }

    public options = { autoHide: false }

    @Input()
    public mobileColumnsConfig: TableColumns

    @Input()
    public animatedFolderID: string | undefined

    @Output()
    public dragStateChange = new EventEmitter<TrackDragState>()

    @ViewChild("container")
    public scrollContainer: ElementRef<HTMLDivElement>

    public selectedItems: (Folder | Track)[] = []
    public backRowSelected = false
    public animatingLikeTrackIDs = new Set<string>()
    public playbackLoadingTrackID: string | undefined
    public draggingItems: (Folder | Track)[] = []
    public dragTargetFolderID: string | undefined
    public moveInProgress = false
    public readonly dragStartDelay: DragStartDelay = {
        touch: 250,
        mouse: 0,
    }

    private suppressClickAfterDrag = false

    public gridColumns: Object
    public gridColumnsTop: Object
    public isMobile$ = new BehaviorSubject<boolean>(false)

    constructor(
        private cd: ChangeDetectorRef,
        private tracksService: TracksService,
        private player: PlayerService
    ) {
        super()
    }

    ngOnInit() {
        // Make sure that the index passed on is correct
        this.columnsConfig.forEach((tc, i) => {
            Helpers.assert(
                this.columnsConfig.findIndex(c => c.index === tc.index) === i,
                "Column configuration indexes are not unique"
            )
        })
        this.columnsConfig.sort((a, b) => a.index - b.index)
        this.styleGrid(false)

        this.subscribeToResize()
        this.initListeners()
    }

    public formatDuration(item: Track): string {
        if (item.loading < 100 || !(item.duration > 0)) {
            return "-"
        }

        const minutes = Math.floor(item.duration / 60)
        const secs = Math.round(item.duration % 60)
        return `${minutes}:${String(secs).padStart(2, "0")}`
    }

    async ngAfterViewInit() {}

    private selectedItemsType(): "folder" | "track" | "multi" | "none" {
        if (this.selectedItems.length === 0) {
            return "none"
        }

        if (this.selectedItems.length > 1) {
            return "multi"
        }

        if (this.isFolder(this.selectedItems[0])) {
            return "folder"
        }

        return "track"
    }

    public startRenaming(event: MouseEvent, item: Folder | Track) {
        this.preventOpen(event)

        this.renamingItem = item
        this.cd.detectChanges()
        this.renameInput.nativeElement.focus()
    }

    public preventOpen(event: MouseEvent) {
        event.preventDefault()
        event.stopImmediatePropagation()
    }

    private createFolderContextMenu(folder: Folder): {
        options: MenuOption<any>[]
        title: string
    } {
        const data: MenuOption<any>[] = [
            {
                data: undefined,
                loading: false,
                icon: "assets/img/menu/open.svg",
                text: "Open",
                enabled: true,
                onClick: (async () => {
                    await this.tracksService.openFolder(folder._id)
                }).bind(this),
            },

            {
                data: undefined,
                icon: "assets/img/menu/track-context-rename.svg",
                loading: false,
                text: "Rename",
                enabled: true,
                onClick: (async () => {
                    this.renamingItem = folder
                    this.cd.detectChanges()
                    this.renameInput.nativeElement.focus()
                }).bind(this),
            },

            {
                data: this.getMoveToMenuOptions(),
                icon: "assets/img/navbar/projects.svg",
                loading: false,
                text: "Move to...",
                onClick: (async () => {}).bind(this),
                enabled: true,
            },
            {
                data: undefined,
                icon: "assets/img/menu/track-context-delete.png",
                buttonClass: "delete",
                loading: false,
                text: "Delete",
                enabled: true,
                onClick: (async () => {
                    await this.tracksService.deleteContent([folder._id], [])
                }).bind(this),
            },
        ]

        return { options: data, title: folder.name }
    }

    private createMultiTypeContextMenu(): {
        options: MenuOption<any>[]
        title: string
    } {
        const data: MenuOption<any>[] = [
            {
                data: undefined,
                icon: "assets/img/folder.svg",
                loading: false,
                text: "New folder with selection",
                enabled: true,
                onClick: (async () => {
                    const folder = await this.tracksService.createFolder()

                    if (folder !== undefined) {
                        const { folders, tracks } = this.getSelectedContent()

                        await this.tracksService.moveContent(
                            folders,
                            tracks,
                            folder._id
                        )
                    }
                }).bind(this),
            },

            {
                data: this.getMoveToMenuOptions(),
                icon: "assets/img/navbar/projects.svg",
                loading: false,
                text: "Move to...",
                onClick: (async () => {}).bind(this),
                enabled: true,
            },
            {
                data: undefined,
                icon: "assets/img/menu/track-context-delete.png",
                buttonClass: "delete",
                loading: false,
                text: "Delete",
                enabled: true,
                onClick: (async () => {
                    const { folders, tracks } = this.getSelectedContent()

                    await this.tracksService.deleteContent(
                        folders.map(f => f._id),
                        tracks.map(t => t._id)
                    )
                }).bind(this),
            },
        ]

        return {
            options: data,
            title: this.selectedItems.length + " elements",
        }
    }

    public async confirmRename() {
        await this.tracksService.rename(
            this.renamingItem,
            this.isFolder(this.renamingItem) ? "folder" : "track",
            this.renameInput.nativeElement.value
        )

        this.renamingItem = undefined
        this.cd.detectChanges()
    }

    private createTrackContextMenu(track: Track): {
        options: MenuOption<any>[]
        title: string
    } {
        const generationInProgress = track.loading !== 100
        const data: MenuOption<any>[] = [
            {
                data: undefined,
                loading: false,
                iconLoading: generationInProgress,
                icon: "assets/img/menu/track-context-download.svg",
                iconWidth: "12px",
                iconHeight: "auto",
                text: "Download",
                onClick: (async () => {
                    await this.tracksService.downloadTrack(track)
                }).bind(this),
                enabled: !generationInProgress,
            },

            {
                data: undefined,
                loading: false,
                icon: track.liked
                    ? "assets/img/loved.svg"
                    : "assets/img/love.svg",
                text: "Like",
                onClick: (async () => {
                    const shouldLike = !track.liked
                    await this.tracksService.like(track, shouldLike)

                    this.setLikeAnimation(track, shouldLike)
                }).bind(this),
                enabled: true,
            },

            {
                data: undefined,
                icon: "assets/img/menu/track-context-rename.svg",
                loading: false,
                text: "Rename",
                onClick: (async () => {
                    this.renamingItem = track
                    this.cd.detectChanges()
                    this.renameInput.nativeElement.focus()
                }).bind(this),
                enabled: true,
            },

            {
                data: this.getMoveToMenuOptions(),
                icon: "assets/img/navbar/projects.svg",
                loading: false,
                text: "Move to...",
                onClick: (async (arg, event: MouseEvent) => {}).bind(this),
                enabled: true,
            },
            {
                data: undefined,
                icon: "assets/img/menu/track-context-delete.png",
                buttonClass: "delete",
                loading: false,
                text: "Delete",
                onClick: (async () => {
                    await this.tracksService.deleteContent([], [track._id])
                }).bind(this),
                enabled: true,
            },
        ]

        return { options: data, title: track.name }
    }

    private getMoveToMenuOptions(): MenuOption<any>[] {
        const folders = [...this.tracksService.content$.getValue().folders]

        if (this.tracksService.path.length > 1) {
            const parentFolder =
                this.tracksService.path[this.tracksService.path.length - 2]

            folders.unshift({
                _id: parentFolder.folderID,
                name: "... " + parentFolder.folderName,
            })
        }

        return folders.map(f => {
            return {
                data: undefined,
                icon: undefined,
                loading: false,
                text: f.name,
                enabled: true,
                onClick: (async (arg, event: MouseEvent) => {
                    event.stopImmediatePropagation()
                    event.preventDefault()

                    const { folders, tracks } = this.getSelectedContent()

                    await this.tracksService.moveContent(folders, tracks, f._id)
                }).bind(this),
            }
        })
    }

    private getSelectedContent(): {
        folders: Folder[]
        tracks: Track[]
    } {
        const selectedFolders: Folder[] = this.selectedItems.filter(c =>
            this.isFolder(c)
        )

        const selectedTracks: Track[] = this.selectedItems.filter(c =>
            this.isTrack(c)
        ) as Track[]

        return {
            folders: selectedFolders,
            tracks: selectedTracks,
        }
    }

    public setContextMenu(
        event: MouseEvent,
        value: Folder | Track | undefined
    ) {
        if (this.contextMenu !== undefined || value === undefined) {
            this.contextMenu = undefined
            this.cd.detectChanges()

            return
        }

        this.renamingItem = undefined

        if (!this.isSelected(value)) {
            this.selectedItems = [value]
        }

        event.preventDefault()
        event.stopImmediatePropagation()

        const type = this.selectedItemsType()

        let data

        if (type === "folder") {
            data = this.createFolderContextMenu(value)
        } else if (type === "track") {
            data = this.createTrackContextMenu(value as Track)
        } else {
            data = this.createMultiTypeContextMenu()
        }

        this.contextMenu = {
            options: data.options,
            onClose: () => {
                this.contextMenu = undefined
                this.cd.detectChanges()
            },
            coordinates: {
                x: event.x,
                y: event.y,
            },
        }
    }

    private areEqual(one: Track | Folder, two: Track | Folder): boolean {
        const typeOne = this.isFolder(one) ? "folder" : "track"
        const typeTwo = this.isFolder(two) ? "folder" : "track"

        if (typeOne !== typeTwo) {
            return false
        }

        return one._id === two._id
    }

    public isSelected(value: Track | Folder): boolean {
        return this.selectedItems.find(s => s._id === value._id) !== undefined
    }

    private getItemIndex(item: Track | Folder): number {
        return this.unionedData.findIndex(i => i._id === item._id)
    }

    private aIsBeforeB(a: Track | Folder, b: Track | Folder): boolean {
        return this.getItemIndex(a) < this.getItemIndex(b)
    }

    public selectItem($event: MouseEvent, value: Folder | Track): void {
        if (this.consumeSuppressedClick($event)) {
            return
        }

        // Check that control and shift keys are not pressed
        if ($event.shiftKey) {
            this.deselectAllExceptFirst()
            if (!this.areEqual(this.selectedItems[0], value)) {
                const indexA = this.getItemIndex(value)
                const indexB = this.getItemIndex(this.selectedItems[0])

                if (this.aIsBeforeB(value, this.selectedItems[0])) {
                    for (let j = indexA; j < indexB; ++j) {
                        this.selectedItems.push(this.unionedData[j])
                    }
                } else {
                    for (let j = indexB + 1; j <= indexA; ++j) {
                        this.selectedItems.push(this.unionedData[j])
                    }
                }
            }
        } else if ($event.metaKey || $event.ctrlKey) {
            if (!this.isSelected(value)) {
                this.selectedItems.push(value)
            } else {
                this.deselectOne(value)
            }
        } else {
            if (this.isSelected(value)) {
                this.deselectAll()
                return
            }
            this.deselectAll()
            this.selectedItems.push(value)
        }
    }

    private consumeSuppressedClick(event: MouseEvent): boolean {
        if (!this.suppressClickAfterDrag) {
            return false
        }

        event.preventDefault()
        event.stopImmediatePropagation()
        this.suppressClickAfterDrag = false

        return true
    }

    public async handleRowClick(
        event: MouseEvent,
        item: Folder | Track
    ): Promise<void> {
        if (this.consumeSuppressedClick(event)) {
            return
        }

        this.backRowSelected = false

        if (this.isMobile$.value && this.isFolder(item)) {
            await this.openFolder(event, item as Folder)
            return
        }

        this.selectItem(event, item)
    }

    public parentFolderName(): string {
        if (this.tracksService.path.length > 1) {
            return this.tracksService.path[this.tracksService.path.length - 2]
                .folderName
        }

        return ""
    }

    public parentFolderID(): string {
        if (this.tracksService.path.length > 1) {
            return this.tracksService.path[this.tracksService.path.length - 2]
                .folderID
        }

        return ""
    }

    private currentFolderID(): string {
        return this.tracksService.path[this.tracksService.path.length - 1]
            .folderID
    }

    private emitDragState(isDragging: boolean): void {
        this.dragStateChange.emit({
            isDragging,
            targetFolderID: this.dragTargetFolderID,
        })
    }

    public startDragging(_event: CdkDragStart, item: Folder | Track): void {
        if (!this.isSelected(item)) {
            this.selectedItems = [item]
        }

        this.contextMenu = undefined
        this.renamingItem = undefined
        this.draggingItems = [...this.selectedItems]
        this.dragTargetFolderID = undefined
        this.suppressClickAfterDrag = true

        this.emitDragState(true)
        this.cd.detectChanges()
    }

    public updateDragTarget(event: CdkDragMove): void {
        const element = document.elementFromPoint(
            event.pointerPosition.x,
            event.pointerPosition.y
        )
        const target = element?.closest<HTMLElement>(
            "[data-track-folder-drop-id]"
        )
        const folderID = target?.getAttribute("data-track-folder-drop-id")
        const nextTarget =
            folderID !== null && folderID !== undefined ? folderID : undefined
        const validTarget = this.isValidDropTarget(nextTarget)
            ? nextTarget
            : undefined

        if (validTarget === this.dragTargetFolderID) {
            return
        }

        this.dragTargetFolderID = validTarget
        this.emitDragState(true)
        this.cd.detectChanges()
    }

    private isValidDropTarget(folderID: string | undefined): boolean {
        if (folderID === undefined || folderID === this.currentFolderID()) {
            return false
        }

        return !this.draggingItems.some(
            item => this.isFolder(item) && item._id === folderID
        )
    }

    public async finishDragging(event: CdkDragEnd): Promise<void> {
        const targetFolderID = this.dragTargetFolderID
        const draggedItems = [...this.draggingItems]

        event.source.reset()
        this.dragTargetFolderID = undefined
        this.draggingItems = []
        this.emitDragState(false)

        setTimeout(() => {
            this.suppressClickAfterDrag = false
        })

        if (
            targetFolderID === undefined ||
            draggedItems.length === 0 ||
            this.moveInProgress
        ) {
            this.cd.detectChanges()
            return
        }

        const { folders, tracks } = this.splitContent(draggedItems)

        this.moveInProgress = true
        this.cd.detectChanges()

        try {
            const moved = await this.tracksService.moveContent(
                folders,
                tracks,
                targetFolderID
            )

            if (moved) {
                this.selectedItems = []
            }
        } finally {
            this.moveInProgress = false
            this.cd.detectChanges()
        }
    }

    public isDragged(item: Folder | Track): boolean {
        return this.draggingItems.some(dragged => this.areEqual(dragged, item))
    }

    public dragPreviewText(): string {
        if (this.draggingItems.length > 1) {
            return `${this.draggingItems.length} elements`
        }

        return this.draggingItems[0]?.name ?? ""
    }

    private splitContent(items: (Folder | Track)[]): {
        folders: Folder[]
        tracks: Track[]
    } {
        return {
            folders: items.filter(item => this.isFolder(item)) as Folder[],
            tracks: items.filter(item => this.isTrack(item)) as Track[],
        }
    }

    public getCreationDate(item: Folder | Track): string {
        if (this.isFolder(item)) {
            return ""
        }

        const dateString = new Date((item as Track).createdAt)
            .toDateString()
            .split(" ")
            .slice(1)

        return dateString.join(" ")
    }

    public isInFolder(): boolean {
        return this.tracksService.path.length > 1
    }

    public async openFolder($event: MouseEvent, folder: Folder) {
        if (!this.isFolder(folder)) {
            return
        }
        await this.tracksService.openFolder(folder._id)
        this.deselectAll()
        this.hoveredItem = undefined
        this.cd.detectChanges()
    }

    public async goBack($event: MouseEvent) {
        await this.tracksService.goBackFolder()
        this.deselectAll()
        this.hoveredItem = undefined
        this.cd.detectChanges()
    }

    public async handleBackClick(event: MouseEvent): Promise<void> {
        if (this.isMobile$.value) {
            await this.goBack(event)
            return
        }

        const shouldSelect = !this.backRowSelected
        this.deselectAll()
        this.backRowSelected = shouldSelect
    }

    private deselectAllExceptFirst(): void {
        this.selectedItems = [this.selectedItems[0]]
    }

    public deselectAll(): void {
        this.selectedItems = []
        this.backRowSelected = false
    }

    private deselectOne(value: Track | Folder): void {
        const index = this.selectedItems.findIndex(i => i._id === value._id)

        if (index !== -1) {
            this.selectedItems.splice(index, 1)
        }
    }

    private subscribeToResize(): void {
        const ro = new ResizeObserver(entries => {
            const isMobile = entries[0].contentRect.width < 780

            this.isMobile$.next(isMobile)
            this.styleGrid(isMobile)
        })
        ro.observe(document.body)
    }

    private styleGrid(isMobile: boolean): void {
        if (isMobile) {
            this.gridColumns = {
                "grid-template-columns": "48px minmax(0, 1fr) 44px",
            }
            this.gridColumnsTop = {
                "grid-template-columns": "48px minmax(0, 1fr) 44px",
            }
            this.cd.detectChanges()

            return
        }

        this.gridColumns = {
            "grid-template-columns":
                "64px minmax(180px, 1fr) 100px 140px 44px 44px",
        }
        this.gridColumnsTop = {
            "grid-template-columns":
                "64px minmax(180px, 1fr) 100px 140px 44px 44px",
        }
        this.cd.detectChanges()
    }

    public async handlePlay(item: Track) {
        const canPlayPendingTrack =
            this.tracksService.isPendingTrack(item) &&
            this.player.songIsCached(item._id)

        if (this.tracksService.isPendingTrack(item) && !canPlayPendingTrack) {
            return
        }

        if (this.player.query.currentSong?.song.id === item._id) {
            if (this.player.query.isPlaying) {
                this.player.pause()
            } else {
                this.player.play()
            }
        } else {
            const index = this.tracksService.content$.value.tracks.findIndex(
                t => t._id === item._id
            )

            if (index === -1) {
                return
            }

            const tracksBefore = this.tracksService.content$.value.tracks
                .slice(0, index)
                .filter(
                    track =>
                        !this.tracksService.isPendingTrack(track) ||
                        this.player.songIsCached(track._id)
                )
                .map(track => toSongQueueEntry(track, ""))

            const tracksAfter = this.tracksService.content$.value.tracks
                .slice(index + 1)
                .filter(
                    track =>
                        !this.tracksService.isPendingTrack(track) ||
                        this.player.songIsCached(track._id)
                )
                .map(track => toSongQueueEntry(track, ""))

            const currentSong: CurrentSong = {
                song: toSongQueueEntry(item, ""),
                userQueueOrigin: false,
            }

            const songQueue = new SongQueue(
                [],
                tracksAfter,
                currentSong,
                tracksBefore
            )

            this.playbackLoadingTrackID = item._id
            this.cd.detectChanges()

            try {
                await this.player.setSongQueue(songQueue, true)
            } finally {
                if (this.playbackLoadingTrackID === item._id) {
                    this.playbackLoadingTrackID = undefined
                    this.cd.detectChanges()
                }
            }
        }
    }

    public async toggleLike(event: MouseEvent, item: Track): Promise<void> {
        event.preventDefault()
        event.stopImmediatePropagation()

        const shouldLike = !item.liked
        await this.tracksService.like(item, shouldLike)

        this.setLikeAnimation(item, shouldLike)
        this.cd.detectChanges()
    }

    public finishLikeAnimation(trackID: string): void {
        this.animatingLikeTrackIDs.delete(trackID)
    }

    private setLikeAnimation(track: Track, shouldAnimate: boolean): void {
        if (shouldAnimate) {
            this.animatingLikeTrackIDs.add(track._id)
        } else {
            this.animatingLikeTrackIDs.delete(track._id)
        }
    }

    private getGridColumns(type: "regular" | "top"): Object {
        return this.columnsConfig
            .map(c => {
                switch (c.size) {
                    case "xs":
                        return "1fr"
                    case "s":
                        return "2fr"
                    case "m":
                        return "3fr"
                    case "l":
                        return "8fr"
                    case "xl":
                        return "30fr"
                }
            })
            .join(" ")
    }

    private initListeners(): void {
        this.subscribe(this.tracksService.content$, value => {
            this.tableData = value
            this.refreshOpenTrackContextMenu(value)
            this.cd.detectChanges()
        })

        this.subscribe(this.tracksService.scrollToIndex$, index => {
            this.cdkVirtualScrollViewport.scrollToIndex(index)
        })
    }

    public trackIsPartiallyLoaded(item: Track): boolean {
        return item.loading === 100 || this.player.songIsCached(item._id)
    }

    private refreshOpenTrackContextMenu(data: GetContentResponse): void {
        if (
            this.contextMenu === undefined ||
            this.selectedItemsType() !== "track"
        ) {
            return
        }

        const selectedTrack = this.selectedItems[0] as Track
        const currentTrack = data.tracks.find(
            track => track._id === selectedTrack._id
        )

        if (currentTrack === undefined) {
            this.contextMenu = undefined
            return
        }

        this.selectedItems = [currentTrack]
        this.contextMenu = {
            ...this.contextMenu,
            options: this.createTrackContextMenu(currentTrack).options,
        }
    }
}
